import React, { useEffect, useState, useMemo, useCallback } from "react";

import {
  collection,
  addDoc,
  doc,
  deleteDoc,
  updateDoc,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  increment,
} from "firebase/firestore";
import { db } from "../firebase";
import { toast } from "react-toastify";
import { useMyContext } from "../Context/MyContext";
import {
  FaHeart,
  FaShareAlt,
  FaFacebook,
  FaInstagram,
  FaTwitter,
  FaWhatsapp,
  FaCouch,
} from "react-icons/fa";
import { v4 as uuidv4 } from "uuid";
import { motion, AnimatePresence } from "framer-motion";

// CHAREX-SPECIFIC FIRESTORE/CLOUDINARY SETTINGS
// Firestore: charex/{productId}
const CHAREX_PRODUCTS_COLLECTION = ["charex"];

// Create React App environment variables
const CLOUDINARY_CLOUD_NAME =
  process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const CLOUDINARY_UPLOAD_PRESET =
  process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

const CHAREX_CLOUDINARY_ROOT = "charex/cookingitems";

export default function CookingItems() {
  // ---------- Local UI & form state ----------
  const [showAddedModalItemId, setShowAddedModalItemId] = useState(null);

  const [newCookingItem, setNewCookingItem] = useState({
    name: "",
    description: "",
    priceOriginal: "",
    priceDiscounted: "",
    image: null,
    category: "",
    sizes: [],
    colors: [],
    colorImages: {},
    sizeImages: {},
    available: true,
    status: "Available",
    trackingStage: "Pending",
    wishes: 0,
    wishedBy: [],
    shipmentFee: "",
    quantity: 0,
    restockAmount: 0,
  });

  const [colorFiles, setColorFiles] = useState({});
  const [sizeFiles, setSizeFiles] = useState({});
  const [sizeInput, setSizeInput] = useState("");
  const [colorInput, setColorInput] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCookingItem, setEditingCookingItem] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [activeColor, setActiveColor] = useState(null);
  const [activeSize, setActiveSize] = useState(null);
  const [selectedQty, setSelectedQty] = useState(1);
  const [loadingItemId, setLoadingItemId] = useState(null);
  const [tooltipVisible, setTooltipVisible] = useState({});
  const [visibleCount, setVisibleCount] = useState(6);
  const [isSavingProduct, setIsSavingProduct] = useState(false);
  const [deletingItemId, setDeletingItemId] = useState(null);

  // ✅ Local wishlist for guests (NO Firestore writes)
  const [localWishlist, setLocalWishlist] = useState(() => {
    try {
      const raw = localStorage.getItem("local_wishlist_ids");
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  const [customTrackingInput, setCustomTrackingInput] = useState("");
  const [addModalCustomVisible, setAddModalCustomVisible] = useState(false);
  const [savedScroll, setSavedScroll] = useState(0);

  // NEW: track last variant selection type so price display replaces (not adds)
  const [lastVariantSelection, setLastVariantSelection] = useState(null); // "color" | "size" | null

  // ✅ local preview URLs so size image selection works instantly in Add/Edit
  const [sizePreviewUrls, setSizePreviewUrls] = useState({});
  const [colorPreviewUrls, setColorPreviewUrls] = useState({});

  // ---------- Context ----------
  const {
    addToCart,
    filteredCookingItems = [],
    cookingitems = [],
    capitalizeWords,
    statusFilter,
    currentUser,
    isAdmin,
    filterCategory,
    toggleCart,
    refreshCookingItems,
  } = useMyContext();

  // persist local wishlist
  useEffect(() => {
    try {
      localStorage.setItem("local_wishlist_ids", JSON.stringify(localWishlist));
    } catch {}
  }, [localWishlist]);

  // ✅ FIX: Visitors should NEVER see hidden products.
  // Treat missing "available" as visible.
  const visibleToVisitor = useCallback((item) => {
    const itemStatus = normalize(item?.status);

    // Pre-order products must remain visible even when they are not
    // immediately available in stock.
    if (itemStatus === "preorder") {
      return true;
    }

    // Normal products remain controlled by the visibility field.
    return item?.available !== false;
  }, []);

  const baseCookingItems = useMemo(
    () =>
      (cookingitems || []).filter((i) => (isAdmin ? true : visibleToVisitor(i))),
    [cookingitems, isAdmin, visibleToVisitor]
  );

  const baseFilteredCookingItems = useMemo(
    () =>
      (filteredCookingItems || []).filter((i) =>
        isAdmin ? true : visibleToVisitor(i)
      ),
    [filteredCookingItems, isAdmin, visibleToVisitor]
  );

  // categories
  const categories = [
    "Household appliances",
    "Perfume",
    "Back to school",
    "Kids wear & essentials",
    "Ladies wear",
    "Furniture",
    "Bags and luggage",
    "Fashion items",
    "Electronics & gadget",
    "Hairs",
  ];

  // ✅ Category icon helper (so “Furniture” uses the correct icon)
  const getCategoryIcon = (cat) => {
    const c = (cat || "").toString().toLowerCase().trim();
    if (c === "furniture") return <FaCouch className="inline-block mr-2" />;
    return null;
  };

  // ---------- Helpers ----------
  const getName = (maybeObjOrString) =>
    typeof maybeObjOrString === "string"
      ? maybeObjOrString
      : maybeObjOrString?.name ?? null;

  // Normalize strings for status/filter comparisons.
  const normalize = (s) =>
    (s || "").toString().toLowerCase().replace(/[^a-z0-9]+/g, "");

  // ✅ generate local preview URLs for selected files (sizes/colors)
  useEffect(() => {
    const next = {};
    for (const [size, file] of Object.entries(sizeFiles || {})) {
      if (file instanceof File) next[size] = URL.createObjectURL(file);
    }

    setSizePreviewUrls((prev) => {
      Object.entries(prev || {}).forEach(([k, url]) => {
        if (!next[k] || next[k] !== url) {
          try {
            URL.revokeObjectURL(url);
          } catch {}
        }
      });
      return next;
    });

    return () => {
      Object.values(next).forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch {}
      });
    };
  }, [sizeFiles]);
  useEffect(() => {
    const next = {};
    for (const [color, file] of Object.entries(colorFiles || {})) {
      if (file instanceof File) next[color] = URL.createObjectURL(file);
    }

    setColorPreviewUrls((prev) => {
      Object.entries(prev || {}).forEach(([k, url]) => {
        if (!next[k] || next[k] !== url) {
          try {
            URL.revokeObjectURL(url);
          } catch {}
        }
      });
      return next;
    });

    return () => {
      Object.values(next).forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch {}
      });
    };
  }, [colorFiles]);

  // -------------------------
  // Variant-aware stock helpers
  // -------------------------
  const sumRestockedForVariant = (item, variantName) => {
    if (!variantName) return 0;
    if (!item?.restockHistory || !Array.isArray(item.restockHistory)) return 0;

    const nameLower = variantName?.toString().toLowerCase().trim();

    return item.restockHistory
      .filter((r) => {
        if (!r) return false;
        if (r.color && r.color.toString().toLowerCase().trim() === nameLower)
          return true;
        if (r.size && r.size.toString().toLowerCase().trim() === nameLower)
          return true;
        if (
          r.variantName &&
          r.variantName.toString().toLowerCase().trim() === nameLower
        )
          return true;
        if (
          r.variant &&
          r.variant.toString().toLowerCase().trim() === nameLower
        )
          return true;
        if (r.note && r.note.toString().toLowerCase().includes(nameLower))
          return true;
        return false;
      })
      .reduce((s, e) => s + (Number(e?.quantity || 0) || 0), 0);
  };

  const handleQtyEditAttempt = (e) => {
    try {
      if (e && e.preventDefault) e.preventDefault();
      if (e && e.stopPropagation) e.stopPropagation();
    } catch {}

    toast.info(
      "Stock quantities are managed in Inventory. Please use Inventory → Restock."
    );

    try {
      e.target.blur?.();
    } catch {}
  };

  const sumSoldForVariant = (item, variantName) => {
    if (!variantName) return 0;
    if (!item?.soldHistory || !Array.isArray(item.soldHistory)) return 0;

    const nameLower = variantName?.toString().toLowerCase().trim();

    return item.soldHistory
      .filter((r) => {
        if (!r) return false;
        if (r.color && r.color.toString().toLowerCase().trim() === nameLower)
          return true;
        if (r.size && r.size.toString().toLowerCase().trim() === nameLower)
          return true;
        if (
          r.variantName &&
          r.variantName.toString().toLowerCase().trim() === nameLower
        )
          return true;
        if (
          r.variant &&
          r.variant.toString().toLowerCase().trim() === nameLower
        )
          return true;
        if (r.note && r.note.toString().toLowerCase().includes(nameLower))
          return true;
        return false;
      })
      .reduce((s, e) => s + (Number(e?.quantity || 0) || 0), 0);
  };

  const computeTotalStock = (item) => {
    if (!item) return 0;

    const hasRestockHistory =
      Array.isArray(item?.restockHistory) && item.restockHistory.length > 0;
    const hasSoldHistory =
      Array.isArray(item?.soldHistory) && item.soldHistory.length > 0;

    const names = new Set();

    if (Array.isArray(item?.colors)) {
      item.colors.forEach((c) => {
        const n = typeof c === "string" ? c : c?.name;
        if (n) names.add(n.toString().trim());
      });
    }

    if (Array.isArray(item?.sizes)) {
      item.sizes.forEach((s) => {
        const n = typeof s === "string" ? s : s?.name;
        if (n) names.add(n.toString().trim());
      });
    }

    (item?.restockHistory || []).forEach((r) => {
      const n = r?.color || r?.size || r?.variantName || r?.variant;
      if (n) names.add(n.toString().trim());
    });

    (item?.soldHistory || []).forEach((s) => {
      const n = s?.color || s?.size || s?.variantName || s?.variant;
      if (n) names.add(n.toString().trim());
    });

    if (names.size > 0 && (hasRestockHistory || hasSoldHistory)) {
      let total = 0;
      names.forEach((name) => {
        const restocked = sumRestockedForVariant(item, name);
        const sold = sumSoldForVariant(item, name);
        total += Math.max(0, restocked - sold);
      });
      return Math.max(0, total);
    }

    if (hasRestockHistory || hasSoldHistory) {
      const restockedTotal = (item?.restockHistory || []).reduce(
        (acc, r) => acc + (Number(r?.quantity || 0) || 0),
        0
      );
      const soldTotal = (item?.soldHistory || []).reduce(
        (acc, s) => acc + (Number(s?.quantity || 0) || 0),
        0
      );
      return Math.max(0, restockedTotal - soldTotal);
    }

    return Number(item.quantity || 0);
  };

  const getVariantQuantity = (
    item,
    colorName,
    sizeName,
    lastSelection = null
  ) => {
    if (!item) return 0;

    const hasVariantsFlag =
      (Array.isArray(item?.colors) && item.colors.length > 0) ||
      (Array.isArray(item?.sizes) && item.sizes.length > 0);

    const hasHistoryFlag =
      (Array.isArray(item?.restockHistory) && item.restockHistory.length > 0) ||
      (Array.isArray(item?.soldHistory) && item.soldHistory.length > 0);

    if (!hasVariantsFlag && !hasHistoryFlag) return Number(item.quantity || 0);

    let variantName = null;

    if (lastSelection === "size") {
      variantName = (sizeName && sizeName.toString().trim()) || null;
      if (!variantName)
        variantName = (colorName && colorName.toString().trim()) || null;
    } else if (lastSelection === "color") {
      variantName = (colorName && colorName.toString().trim()) || null;
      if (!variantName)
        variantName = (sizeName && sizeName.toString().trim()) || null;
    } else {
      variantName =
        (colorName && colorName.toString().trim()) ||
        (sizeName && sizeName.toString().trim()) ||
        null;
    }

    if (variantName) {
      const restocked = sumRestockedForVariant(item, variantName);
      const sold = sumSoldForVariant(item, variantName);
      return Math.max(0, restocked - sold);
    }

    return computeTotalStock(item);
  };

  const clampQtyToStock = (qty, stock) => {
    const q = Number(qty || 1);
    const s = Number(stock || 0);
    if (!Number.isFinite(q) || q <= 0) return 1;
    if (!Number.isFinite(s) || s <= 0) return 1;
    return Math.min(q, s);
  };

  const getSelectedItemCurrentStock = (item) => {
    if (!item) return 0;
    return getVariantQuantity(
      item,
      activeColor,
      activeSize,
      lastVariantSelection
    );
  };

  // ---------- Other helpers ----------
  const hasVariants = (item) =>
    (Array.isArray(item?.colors) && item.colors.length > 0) ||
    (Array.isArray(item?.sizes) && item.sizes.length > 0);

  const getVariantPrice = (item, colorName, sizeName, lastSelection = null) => {
    const toNum = (v) => {
      if (v === null || v === undefined || v === "") return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };

    const basePrice =
      toNum(item.priceDiscounted) ?? toNum(item.priceOriginal) ?? 0;

    if (!hasVariants(item)) return basePrice;

    const sizeObj =
      sizeName && (item.sizes || []).find((s) => getName(s) === sizeName);

    const colorObj =
      colorName && (item.colors || []).find((c) => getName(c) === colorName);

    const sizePrice =
      sizeObj &&
      (toNum(sizeObj.priceDiscounted) ?? toNum(sizeObj.priceOriginal));

    const colorPrice =
      colorObj &&
      (toNum(colorObj.priceDiscounted) ?? toNum(colorObj.priceOriginal));

    if (lastSelection === "size") return sizePrice ?? colorPrice ?? basePrice;
    if (lastSelection === "color") return colorPrice ?? sizePrice ?? basePrice;

    return sizePrice ?? colorPrice ?? basePrice;
  };

  const getImageForColor = (item, color) => {
    const colorName = getName(color);
    return (
      (item.colorImages && item.colorImages[colorName]) ||
      item.image ||
      "/placeholder.jpg"
    );
  };

  const getImageForVariant = (
    item,
    colorName,
    sizeName,
    lastSelection = null
  ) => {
    if (!item) return "/placeholder.jpg";

    const c = colorName ? String(colorName).trim() : null;
    const s = sizeName ? String(sizeName).trim() : null;

    const sizeUrl = s ? item?.sizeImages?.[s] : null;
    const colorUrl = c ? item?.colorImages?.[c] : null;

    if (lastSelection === "size")
      return sizeUrl || colorUrl || item.image || "/placeholder.jpg";
    if (lastSelection === "color")
      return colorUrl || sizeUrl || item.image || "/placeholder.jpg";

    return sizeUrl || colorUrl || item.image || "/placeholder.jpg";
  };

  // ---------- Input Handlers ----------
  const handleInputChange = (e) => {
    const { name, value, files, type, checked } = e.target;

    if (name === "image") {
      setNewCookingItem((prev) => ({ ...prev, image: files[0] || null }));
    } else if (name && name.startsWith("color-file-")) {
      const color = name.replace("color-file-", "");
      const file = files[0] || null;
      setColorFiles((prev) => ({ ...prev, [color]: file }));
    } else if (name && name.startsWith("size-file-")) {
      const size = name.replace("size-file-", "");
      const file = files[0] || null;
      setSizeFiles((prev) => ({ ...prev, [size]: file }));
    } else if (type === "checkbox") {
      setNewCookingItem((prev) => ({ ...prev, [name]: checked }));
    } else {
      setNewCookingItem((prev) => ({ ...prev, [name]: value }));
      if (name === "trackingStage") {
        if (value === "Custom") setAddModalCustomVisible(true);
        else setAddModalCustomVisible(false);
      }
    }
  };

  const addArrayValue = (type, value) => {
    if (!value || !value.toString().trim()) return;

    const v = value.toString().trim();

    setNewCookingItem((prev) => {
      const arr = prev[type] || [];
      if (arr.some((x) => getName(x) === v)) return prev;

      const newEntry = {
        name: v,
        priceOriginal: "",
        priceDiscounted: "",
        quantity: 0,
      };

      const next = {
        ...prev,
        [type]: [...arr, newEntry],
      };

      if (type === "colors") {
        next.colorImages = {
          ...(prev.colorImages || {}),
          [v]: prev.colorImages?.[v] || null,
        };
      }

      if (type === "sizes") {
        next.sizeImages = {
          ...(prev.sizeImages || {}),
          [v]: prev.sizeImages?.[v] || null,
        };
      }

      return next;
    });

    if (type === "sizes") setSizeInput("");
    if (type === "colors") setColorInput("");
  };

  const removeArrayValue = (type, name) => {
    setNewCookingItem((prev) => {
      const next = {
        ...prev,
        [type]: (prev[type] || []).filter((x) => getName(x) !== name),
      };

      if (type === "colors") {
        const imgs = { ...(next.colorImages || {}) };
        delete imgs[name];
        next.colorImages = imgs;
      }

      if (type === "sizes") {
        const imgs = { ...(next.sizeImages || {}) };
        delete imgs[name];
        next.sizeImages = imgs;
      }

      return next;
    });

    setColorFiles((prev) => {
      const c = { ...prev };
      delete c[name];
      return c;
    });

    setSizeFiles((prev) => {
      const s = { ...prev };
      delete s[name];
      return s;
    });
  };

  // Refresh Firestore products immediately after every CRUD mutation.
  // force=true bypasses the ten-minute product cache in MyContext.
  const refreshProductsAfterMutation = async () => {
    try {
      localStorage.removeItem("charex_cookingitems_cache");
      localStorage.removeItem("charex_cookingitems_cache_time");
    } catch {}

    if (typeof refreshCookingItems === "function") {
      await refreshCookingItems(true);
    }
  };

  // ✅ Cloudinary upload helper for Create React App
  // Firestore stores only the returned secure Cloudinary URL.
  const uploadFileAndGetUrl = async (folder, file) => {
    if (!(file instanceof File)) {
      throw new Error("Please select a valid image file.");
    }

    if (!file.type?.startsWith("image/")) {
      throw new Error("Only image files can be uploaded.");
    }

    if (file.size > 10 * 1024 * 1024) {
      throw new Error("Image size must not exceed 10MB.");
    }

    if (!CLOUDINARY_CLOUD_NAME) {
      throw new Error(
        "REACT_APP_CLOUDINARY_CLOUD_NAME is missing from your .env file."
      );
    }

    if (!CLOUDINARY_UPLOAD_PRESET) {
      throw new Error(
        "REACT_APP_CLOUDINARY_UPLOAD_PRESET is missing from your .env file."
      );
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);
    formData.append("folder", folder);

    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
      {
        method: "POST",
        body: formData,
      }
    );

    const result = await response.json();

    if (!response.ok) {
      throw new Error(
        result?.error?.message || "Cloudinary image upload failed."
      );
    }

    if (!result?.secure_url) {
      throw new Error("Cloudinary did not return a secure image URL.");
    }

    return result.secure_url;
  };

  // ---------- Add / Update ----------
  const handleSubmitCookingItem = async () => {
    const {
      name,
      description,
      priceOriginal,
      priceDiscounted,
      shipmentFee,
      image,
      category,
      available,
      sizes,
      colors,
      colorImages,
      status,
      trackingStage,
      quantity,
      restockAmount,
    } = newCookingItem;

    const safeName = name?.toString().trim() || "Untitled Product";
    const safeDescription = description?.toString().trim() || "";
    const safePriceOriginal =
      priceOriginal === "" || priceOriginal === undefined ? null : priceOriginal;
    const safePriceDiscounted =
      priceDiscounted === "" || priceDiscounted === undefined
        ? null
        : priceDiscounted;
    const safeShipmentFee =
      shipmentFee === "" || shipmentFee === undefined ? null : shipmentFee;
    const safeCategory = category?.toString().trim() || "Uncategorized";
    const safeStatus = status || "Available";
    const safeTrackingStage = trackingStage || "Pending";

    setIsSavingProduct(true);

    try {
      const sanitize = (obj) =>
        Object.fromEntries(
          Object.entries(obj).filter(([_, v]) => v !== undefined)
        );

      // ✅ avoid extra reads: detect existing from already-loaded items
      const existing =
        name && name.toString().trim()
          ? (baseCookingItems || []).find(
              (i) =>
                i.name?.toString().trim().toLowerCase() ===
                name.toString().trim().toLowerCase()
            )
          : null;

      // -------------------------
      // RESTOCK EXISTING (no edit mode)
      // -------------------------
      if (!editingCookingItem && existing) {
        const parsedNewColors = (colors || []).map((c) => ({
          name: getName(c),
          priceOriginal: c.priceOriginal || "",
          priceDiscounted: c.priceDiscounted || "",
          quantity: Number(c.quantity || 0),
        }));

        const parsedNewSizes = (sizes || []).map((s) => ({
          name: getName(s),
          priceOriginal: s.priceOriginal || "",
          priceDiscounted: s.priceDiscounted || "",
          quantity: Number(s.quantity || 0),
        }));

        const qtyToAddTopLevel = parseInt(quantity || 0, 10);

        let totalDelta = 0;
        const existingColors = existing.colors || [];
        const existingSizes = existing.sizes || [];

        const mergedColorsMap = {};
        existingColors.forEach((c) => {
          mergedColorsMap[getName(c)] = {
            ...c,
            quantity: Number(c.quantity || 0),
          };
        });

        parsedNewColors.forEach((nc) => {
          const key = nc.name;
          if (!key) return;
          const existingC = mergedColorsMap[key];
          if (existingC) {
            if (nc.quantity > 0) {
              mergedColorsMap[key] = {
                ...existingC,
                priceOriginal: nc.priceOriginal || existingC.priceOriginal,
                priceDiscounted: nc.priceDiscounted || existingC.priceDiscounted,
                quantity: Number(existingC.quantity || 0) + nc.quantity,
              };
              totalDelta += nc.quantity;
            } else {
              mergedColorsMap[key] = {
                ...existingC,
                priceOriginal: nc.priceOriginal || existingC.priceOriginal,
                priceDiscounted: nc.priceDiscounted || existingC.priceDiscounted,
              };
            }
          } else {
            mergedColorsMap[key] = {
              name: key,
              priceOriginal: nc.priceOriginal || "",
              priceDiscounted: nc.priceDiscounted || "",
              quantity: nc.quantity || 0,
            };
            totalDelta += nc.quantity || 0;
          }
        });

        const mergedSizesMap = {};
        existingSizes.forEach((s) => {
          mergedSizesMap[getName(s)] = {
            ...s,
            quantity: Number(s.quantity || 0),
          };
        });

        parsedNewSizes.forEach((ns) => {
          const key = ns.name;
          if (!key) return;
          const existingS = mergedSizesMap[key];
          if (existingS) {
            if (ns.quantity > 0) {
              mergedSizesMap[key] = {
                ...existingS,
                priceOriginal: ns.priceOriginal || existingS.priceOriginal,
                priceDiscounted: ns.priceDiscounted || existingS.priceDiscounted,
                quantity: Number(existingS.quantity || 0) + ns.quantity,
              };
              totalDelta += ns.quantity;
            } else {
              mergedSizesMap[key] = {
                ...existingS,
                priceOriginal: ns.priceOriginal || existingS.priceOriginal,
                priceDiscounted: ns.priceDiscounted || existingS.priceDiscounted,
              };
            }
          } else {
            mergedSizesMap[key] = {
              name: key,
              priceOriginal: ns.priceOriginal || "",
              priceDiscounted: ns.priceDiscounted || "",
              quantity: ns.quantity || 0,
            };
            totalDelta += ns.quantity || 0;
          }
        });

        if (totalDelta === 0 && qtyToAddTopLevel > 0) {
          totalDelta = qtyToAddTopLevel;
        }

        if (!totalDelta || totalDelta <= 0) {
          toast.error(
            "Product already exists. Enter a positive quantity to restock."
          );
          setEditingCookingItem(existing);
          setNewCookingItem({
            ...existing,
            image: existing.image || null,
            trackingStage: existing.trackingStage || "Pending",
            quantity: existing.quantity ?? 0,
            restockAmount: 0,
          });
          setShowAddModal(true);
          return;
        }

        const finalColors = Object.values(mergedColorsMap);
        const finalSizes = Object.values(mergedSizesMap);

        // ✅ uploads (only when files exist)
        const finalColorImages = { ...(existing.colorImages || {}) };
        for (const [color, file] of Object.entries(colorFiles || {})) {
          if (!file) continue;
          const url = await uploadFileAndGetUrl(
            `${CHAREX_CLOUDINARY_ROOT}/color-images`,
            file
          );
          finalColorImages[color] = url;
        }

        const finalSizeImages = { ...(existing.sizeImages || {}) };
        for (const [size, file] of Object.entries(sizeFiles || {})) {
          if (!file) continue;
          const url = await uploadFileAndGetUrl(
            `${CHAREX_CLOUDINARY_ROOT}/size-images`,
            file
          );
          finalSizeImages[size] = url;
        }

        const itemRef = doc(db, ...CHAREX_PRODUCTS_COLLECTION, existing.id);

        const restockEntry = {
          id: crypto?.randomUUID ? crypto.randomUUID() : uuidv4(),
          quantity: totalDelta,
          timestamp: new Date(),
          adminId: currentUser?.uid || "Unknown Admin",
          note: `Variant restock for ${safeName}`,
        };

        await updateDoc(
          itemRef,
          sanitize({
            colors: finalColors,
            sizes: finalSizes,
            colorImages: finalColorImages,
            sizeImages: finalSizeImages,
            available: true,
            restockHistory: arrayUnion(restockEntry),
            quantity: increment(totalDelta),
            updatedAt: serverTimestamp(),
          })
        );

        await refreshProductsAfterMutation();

        toast.success(`Restocked existing product by ${totalDelta} units`);

        setNewCookingItem({
          name: "",
          description: "",
          priceOriginal: "",
          priceDiscounted: "",
          shipmentFee: "",
          category: "",
          sizes: [],
          colors: [],
          colorImages: {},
          sizeImages: {},
          available: true,
          status: "Available",
          trackingStage: "Pending",
          quantity: 0,
          restockAmount: 0,
          image: null,
        });
        setEditingCookingItem(null);
        setColorFiles({});
        setSizeFiles({});
        setShowAddModal(false);
        return;
      }

      // -------------------------
      // ADD / EDIT
      // -------------------------
      const docRef = editingCookingItem
        ? doc(db, ...CHAREX_PRODUCTS_COLLECTION, editingCookingItem.id)
        : null;

      let mainImageUrl = editingCookingItem?.image || null;

      if (image instanceof File) {
        mainImageUrl = await uploadFileAndGetUrl(
          CHAREX_CLOUDINARY_ROOT,
          image
        );
      }

      const finalColorImages = { ...(colorImages || {}) };
      for (const [color, file] of Object.entries(colorFiles || {})) {
        if (!file) continue;
        const url = await uploadFileAndGetUrl(
          `${CHAREX_CLOUDINARY_ROOT}/color-images`,
          file
        );
        finalColorImages[color] = url;
      }

      const finalSizeImages = { ...(newCookingItem.sizeImages || {}) };
      for (const [size, file] of Object.entries(sizeFiles || {})) {
        if (!file) continue;
        const url = await uploadFileAndGetUrl(
          `${CHAREX_CLOUDINARY_ROOT}/size-images`,
          file
        );
        finalSizeImages[size] = url;
      }

      const updatePayload = {
        name: safeName,
        description: safeDescription,
        priceOriginal: safePriceOriginal,
        priceDiscounted: safePriceDiscounted,
        shipmentFee: safeShipmentFee,
        category: safeCategory,
        available,
        sizes,
        colors,
        colorImages: finalColorImages,
        sizeImages: finalSizeImages,
        status: safeStatus,
        trackingStage: safeTrackingStage,
        image: mainImageUrl,
        updatedAt: serverTimestamp(),
      };

      if (editingCookingItem) {
        const addQtyFromRestock = parseInt(restockAmount || 0, 10);
        if (addQtyFromRestock > 0) {
          updatePayload.quantity = increment(addQtyFromRestock);
          updatePayload.restockHistory = arrayUnion({
            id: crypto?.randomUUID ? crypto.randomUUID() : uuidv4(),
            quantity: addQtyFromRestock,
            timestamp: new Date(),
            adminId: currentUser?.uid || "Unknown Admin",
            note: "Admin restock",
          });
        }

        await updateDoc(docRef, sanitize(updatePayload));
        toast.success("Product Updated ✔");
      } else {
        let computedQty = 0;
        if ((colors && colors.length > 0) || (sizes && sizes.length > 0)) {
          const sumColors = (colors || []).reduce(
            (acc, c) => acc + (Number(c.quantity || 0) || 0),
            0
          );
          const sumSizes = (sizes || []).reduce(
            (acc, s) => acc + (Number(s.quantity || 0) || 0),
            0
          );
          computedQty = Math.max(sumColors, sumSizes);
        } else {
          computedQty = parseInt(quantity, 10) || 0;
        }

        await addDoc(
          collection(db, ...CHAREX_PRODUCTS_COLLECTION),
          sanitize({
            ...updatePayload,
            quantity: computedQty,
            createdAt: serverTimestamp(),
            restockHistory: [
              {
                id: crypto?.randomUUID ? crypto.randomUUID() : uuidv4(),
                quantity: computedQty,
                timestamp: new Date(),
                adminId: currentUser?.uid || "Unknown Admin",
              },
            ],
          })
        );

        toast.success("Product Added ✔");
      }

      await refreshProductsAfterMutation();

      if (editingCookingItem && selectedItem?.id === editingCookingItem.id) {
        setSelectedItem(null);
      }

      setNewCookingItem({
        name: "",
        description: "",
        priceOriginal: "",
        priceDiscounted: "",
        shipmentFee: null,
        category: "",
        sizes: [],
        colors: [],
        colorImages: {},
        sizeImages: {},
        available: true,
        status: "Available",
        trackingStage: "Pending",
        quantity: 0,
        restockAmount: 0,
        image: null,
      });

      setEditingCookingItem(null);
      setColorFiles({});
      setSizeFiles({});
      setSizeInput("");
      setColorInput("");
      setShowAddModal(false);
      setAddModalCustomVisible(false);
      setCustomTrackingInput("");
    } catch (error) {
      console.error("Product save error:", error);
      toast.error(
        error?.message || "Something went wrong while saving the product."
      );
    } finally {
      setIsSavingProduct(false);
    }
  };

  // ---------- Delete / Visibility ----------
  const confirmDeleteToast = async (item) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;

    setDeletingItemId(item.id);

    try {
      await deleteDoc(doc(db, ...CHAREX_PRODUCTS_COLLECTION, item.id));
      await refreshProductsAfterMutation();

      toast.success("Product deleted successfully");
      setSelectedItem(null);
    } catch (err) {
      console.error("Product delete error:", err);
      toast.error(err?.message || "Failed to delete product");
    } finally {
      setDeletingItemId(null);
    }
  };

  const toggleVisibility = async (item) => {
    try {
      const nextAvailable = !item.available;

      await updateDoc(doc(db, ...CHAREX_PRODUCTS_COLLECTION, item.id), {
        available: nextAvailable,
        updatedAt: serverTimestamp(),
      });

      setSelectedItem((prev) =>
        prev ? { ...prev, available: nextAvailable } : prev
      );

      await refreshProductsAfterMutation();

      toast.info(
        `Product is now ${nextAvailable ? "visible" : "hidden"} to visitors`
      );
    } catch (err) {
      toast.error("Failed to toggle visibility");
    }
  };

  // ---------- Add to Cart ----------
  const handleAddToCart = async (
    item,
    color = null,
    size = null,
    quantity = 1
  ) => {
    setLoadingItemId(item.id);

    try {
      const hasVariantsFlag =
        (Array.isArray(item?.colors) && item.colors.length > 0) ||
        (Array.isArray(item?.sizes) && item.sizes.length > 0);

      const parentId = item.parentId || item.id;
      const parentItem = (baseCookingItems || []).find((i) => i.id === parentId);

      const parentQty =
        parentItem && !hasVariantsFlag
          ? Number(computeTotalStock(parentItem)) || 0
          : null;

      const isPreOrder = normalize(item.status || "") === "preorder";

      if (parentQty !== null && parentQty <= 0 && !isPreOrder) {
        toast.error("This item is out of stock and cannot be added to cart.");
        return false;
      }

      const chosenColor = hasVariantsFlag
        ? typeof color === "string"
          ? color
          : getName(color) || null
        : null;

      const chosenSize = hasVariantsFlag
        ? typeof size === "string"
          ? size
          : getName(size) || null
        : null;

      if (hasVariantsFlag) {
        const availableQty = getVariantQuantity(
          item,
          chosenColor,
          chosenSize,
          lastVariantSelection
        );

        if (availableQty < Number(quantity || 1) && !isPreOrder) {
          toast.error(
            "Requested quantity exceeds available stock for this variant."
          );
          return false;
        }
      }

      const variantImage = hasVariantsFlag
        ? getImageForVariant(item, chosenColor, chosenSize, lastVariantSelection)
        : item.image;

      const variantId = hasVariantsFlag
        ? [item.id, chosenColor, chosenSize].filter(Boolean).join("-")
        : item.id;

      const resolvedPrice = hasVariantsFlag
        ? getVariantPrice(item, chosenColor, chosenSize, lastVariantSelection)
        : Number(item.priceDiscounted) ?? Number(item.priceOriginal) ?? null;

      if (resolvedPrice === null) {
        toast.error("Price could not be resolved for this item.");
        return false;
      }

      const variants =
        hasVariantsFlag && (chosenColor || chosenSize)
          ? {
              ...(chosenColor ? { color: chosenColor } : {}),
              ...(chosenSize ? { size: chosenSize } : {}),
            }
          : null;

      const variantText =
        variants && Object.keys(variants).length
          ? Object.entries(variants)
              .map(([k, v]) => `${k}: ${v}`)
              .join(", ")
          : "";

      const itemWithVariant = {
        ...item,
        id: variantId,
        parentId: item.id,
        ...(chosenColor ? { selectedColor: chosenColor } : {}),
        ...(chosenSize ? { selectedSize: chosenSize } : {}),
        ...(variants ? { variants } : {}),
        variantText,
        variantImage,
        image: variantImage,
        quantity: Number(quantity) || 1,
        addedAt: Date.now(),
        status: item.status || "Available",
        variantPrice: resolvedPrice,
        price: resolvedPrice,
      };

      addToCart(itemWithVariant, resolvedPrice);

      toast.success(
        `✅ ${item.name} added to cart${
          hasVariantsFlag
            ? ` (${quantity} × ${chosenColor || ""} ${chosenSize || ""})`
            : ` (${quantity})`
        }`
      );

      return true;
    } catch (err) {
      toast.error("Something went wrong while adding to cart");
      return false;
    } finally {
      setLoadingItemId(null);
    }
  };

  const handleGridAddClick = (e, item) => {
    e.stopPropagation();

    if (hasVariants(item)) {
      openDetail(item);
      return;
    }

    handleAddToCart(item, null, null, 1);
  };

  // ---------- Share ----------
  const shareItem = async (item) => {
    const itemUrl = `${window.location.origin}/item/${encodeURIComponent(
      item.id
    )}`;

    const title = item?.name ? capitalizeWords(item.name) : "Product";
    const text = item?.description
      ? String(item.description).slice(0, 140)
      : "Check out this product";

    try {
      if (navigator.share) {
        await navigator.share({ title, text, url: itemUrl });
        return;
      }

      await navigator.clipboard.writeText(itemUrl);
      toast.info("Link copied to clipboard!");
    } catch (e) {
      try {
        await navigator.clipboard.writeText(itemUrl);
        toast.info("Link copied to clipboard!");
      } catch {
        toast.error("Unable to share. Please copy the link manually.");
      }
    }
  };

  // ---------- Wishlist ----------
  const handleWish = async (item) => {
    // guest wishlist (local)
    if (!currentUser?.uid) {
      setLocalWishlist((prev) => {
        const exists = prev.includes(item.id);
        const next = exists ? prev.filter((x) => x !== item.id) : [...prev, item.id];
        toast.success(exists ? "Removed from wishlist!" : "Added to wishlist!");
        return next;
      });
      return;
    }

    try {
      const userId = currentUser.uid;
      const itemRef = doc(db, ...CHAREX_PRODUCTS_COLLECTION, item.id);

      const alreadyWished = (item.wishedBy || []).includes(userId);

      await updateDoc(itemRef, {
        wishedBy: alreadyWished ? arrayRemove(userId) : arrayUnion(userId),
        wishes: increment(alreadyWished ? -1 : 1),
        updatedAt: serverTimestamp(),
      });

      // Optimistic UI update (no read)
      if (selectedItem?.id === item.id) {
        setSelectedItem((prev) => {
          if (!prev) return prev;

          const prevWishedBy = Array.isArray(prev.wishedBy) ? prev.wishedBy : [];
          const nextWishedBy = alreadyWished
            ? prevWishedBy.filter((x) => x !== userId)
            : [...prevWishedBy, userId];

          const nextWishes = Math.max(
            0,
            Number(prev.wishes || 0) + (alreadyWished ? -1 : 1)
          );

          return { ...prev, wishedBy: nextWishedBy, wishes: nextWishes };
        });
      }

      toast.success(alreadyWished ? "Removed from wishlist!" : "Added to wishlist!");
    } catch (err) {
      toast.error("Failed to update wishlist");
    }
  };

  // ---------- Tooltip ----------
  useEffect(() => {
    const intervals = [];
    const itemsForTooltip = (baseCookingItems || []).slice(0, 24);
    itemsForTooltip.forEach((item) => {
      const startDelay = Math.random() * 5000;
      const loop = () => {
        setTooltipVisible((prev) => ({ ...prev, [item.id]: true }));
        setTimeout(
          () => setTooltipVisible((prev) => ({ ...prev, [item.id]: false })),
          2500
        );
        const nextDelay = 15000 + Math.random() * 5000;
        intervals.push(setTimeout(loop, nextDelay));
      };
      intervals.push(setTimeout(loop, startDelay));
    });
    return () => intervals.forEach((i) => clearTimeout(i));
  }, [baseCookingItems]);

  // ---------- Filtering ----------
  const itemsToDisplay = useMemo(() => {
    const normalizedFilter = normalize(statusFilter);

    if (normalizedFilter === "wishlist") {
      return (baseCookingItems || []).filter((item) => {
        const signedInWish =
          currentUser?.uid && (item.wishedBy || []).includes(currentUser.uid);
        const guestWish = localWishlist.includes(item.id);
        return signedInWish || guestWish;
      });
    }

    // MyContext already applies status, category, and search filters.
    // Never fall back to every product when the filtered result is empty.
    if (normalizedFilter && normalizedFilter !== "all") {
      return baseFilteredCookingItems || [];
    }

    // For "All", retain category/search filtering from context.
    return baseFilteredCookingItems || baseCookingItems || [];
  }, [
    statusFilter,
    baseCookingItems,
    baseFilteredCookingItems,
    currentUser,
    localWishlist,
  ]);

  const visibleItems = useMemo(
    () => itemsToDisplay.slice(0, visibleCount),
    [itemsToDisplay, visibleCount]
  );

  const openEditModal = (item) => {
    setEditingCookingItem(item);
    setNewCookingItem({
      ...item,
      image: item.image || null,
      trackingStage: item.trackingStage || "Pending",
      quantity: item.quantity ?? 0,
      restockAmount: 0,
      colorImages: item.colorImages || {},
      sizeImages: item.sizeImages || {},
    });
    setAddModalCustomVisible(false);
    setShowAddModal(true);
  };

  const openDetail = (item) => {
    const isPreOrder = normalize(item?.status) === "preorder";

    if (!isAdmin && item?.available === false && !isPreOrder) {
      toast.info("This product is currently hidden.");
      return;
    }

    setSavedScroll(window.scrollY);
    setSelectedItem(item);
    setActiveColor(getName(item.colors?.[0]) || null);
    setActiveSize(null);
    setCustomTrackingInput(item.trackingStage || "");
    setSelectedQty(1);
    setLastVariantSelection(null);
    setShowAddedModalItemId(null);
  };

  const updateSelectedItemTracking = async (newStage) => {
    if (!selectedItem) return;

    if ((selectedItem.trackingStage || "") === newStage) return;

    try {
      await updateDoc(doc(db, ...CHAREX_PRODUCTS_COLLECTION, selectedItem.id), {
        trackingStage: newStage,
        updatedAt: serverTimestamp(),
      });
      setSelectedItem((prev) => ({ ...prev, trackingStage: newStage }));
      await refreshProductsAfterMutation();
      toast.success(`Tracking stage updated to "${newStage}"`);
    } catch (err) {
      toast.error("Failed to update tracking stage");
    }
  };

  // ---------- Render ----------
  return (
    <div id="cookingitems" className="p-4">
      {/* Header */}
      <div className="mb-4 flex flex-col gap-1">
        <span className="text-xl font-bold text-purple-700">
          {getCategoryIcon(filterCategory)}
          {filterCategory || "All Categories"}
        </span>
        <span className="text-gray-700 text-base">Products</span>
      </div>

      {/* Add New for admin */}
      {/* {isAdmin && (
        <div className="mb-6">
          <button
            disabled
            onClick={() => {
              setEditingCookingItem(null);
              setNewCookingItem({
                name: "",
                description: "",
                priceOriginal: "",
                priceDiscounted: "",
                shipmentFee: "",
                image: null,
                category: "",
                sizes: [],
                colors: [],
                colorImages: {},
                sizeImages: {},
                available: true,
                status: "Available",
                trackingStage: "Pending",
                wishes: 0,
                wishedBy: [],
                quantity: 0,
                restockAmount: 0,
              });
              setColorFiles({});
              setSizeFiles({});
              setAddModalCustomVisible(false);
              setShowAddModal(true);
            }}
            className="bg-gray-400 text-white px-4 py-2 rounded-md cursor-not-allowed opacity-70"
          >
            Add New
          </button>
        </div>
      )} */}

{/* Add New for admin */}
{isAdmin && (
  <div className="mb-6">
    <button
      onClick={() => {
        setEditingCookingItem(null);
        setNewCookingItem({
          name: "",
          description: "",
          priceOriginal: "",
          priceDiscounted: "",
          shipmentFee: "",
          image: null,
          category: "",
          sizes: [],
          colors: [],
          colorImages: {},
          sizeImages: {},
          available: true,
          status: "Available",
          trackingStage: "Pending",
          wishes: 0,
          wishedBy: [],
          quantity: 0,
          restockAmount: 0,
        });
        setColorFiles({});
        setSizeFiles({});
        setAddModalCustomVisible(false);
        setShowAddModal(true);
      }}
      className="bg-purple-700 text-white px-4 py-2 rounded-md hover:bg-purple-800"
    >
      Add New
    </button>
  </div>
)}

      {/* Product grid */}
      {itemsToDisplay.length === 0 ? (
        <>
          <div className="grid grid-cols-2 gap-6">
            {(baseCookingItems || []).slice(0, visibleCount).map((item) => {
              const stockTotal = computeTotalStock(item);
              const itemHasVariants = hasVariants(item);

              const isWished =
                (currentUser?.uid &&
                  (item.wishedBy || []).includes(currentUser.uid)) ||
                localWishlist.includes(item.id);

              return (
                <div
                  key={item.id}
                  className="relative bg-white rounded shadow-md cursor-pointer"
                  onClick={() => openDetail(item)}
                >
                  <span
                    className={`absolute top-2 left-2 px-2 py-1 text-xs rounded text-white ${
                      normalize(item.status) === "available"
                        ? "bg-green-600"
                        : "bg-yellow-600"
                    }`}
                  >
                    {item.status}
                  </span>

                  <div className="absolute top-2 right-2 px-2 py-1 text-xs rounded text-white bg-gray-800">
                    {`Stock: ${stockTotal}`}
                  </div>

                  <div className="absolute top-10 right-2 flex flex-col gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleWish(item);
                      }}
                      className={`text-xl transition-colors duration-200 ${
                        isWished
                          ? "text-red-600"
                          : "text-rose-300 hover:text-red-300"
                      }`}
                    >
                      <FaHeart /> {item.wishes || 0}
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        shareItem(item);
                      }}
                      className="text-xl text-green-600 hover:text-green-800 transition-colors duration-200"
                    >
                      <FaShareAlt />
                    </button>
                  </div>

                  <img
                    src={getImageForColor(
                      item,
                      getName(item.colors?.[0]) || item.colors?.[0]
                    )}
                    alt={item.name}
                    className="w-full h-48 object-cover rounded-t"
                  />

                  <div className="p-2 flex flex-col gap-2">
                    <p className="text-center font-bold text-gray-800">
                      {capitalizeWords(item.name)}
                    </p>
                    <p className="text-center text-gray-700 font-semibold">
                      ₦
                      {Number(
                        item.priceDiscounted ?? item.priceOriginal ?? 0
                      ).toLocaleString()}
                    </p>

                    <button
                      onClick={(e) => handleGridAddClick(e, item)}
                      disabled={loadingItemId === item.id}
                      className={`px-3 py-1 rounded text-white ${
                        loadingItemId === item.id
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-purple-700 hover:bg-purple-800"
                      }`}
                    >
                      {loadingItemId === item.id
                        ? "Adding..."
                        : itemHasVariants
                        ? "Select Options"
                        : "Add to Cart"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {visibleCount < (baseCookingItems?.length || 0) && (
            <div className="flex justify-center mt-6">
              <button
                onClick={() => setVisibleCount((prev) => prev + 6)}
                className="px-6 py-2 bg-purple-700 text-white rounded-md hover:bg-purple-800 transition-colors"
              >
                Load More
              </button>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-6">
            {(visibleItems || []).map((item) => {
              const stockTotal = computeTotalStock(item);
              const itemHasVariants = hasVariants(item);

              const isWished =
                (currentUser?.uid &&
                  (item.wishedBy || []).includes(currentUser.uid)) ||
                localWishlist.includes(item.id);

              return (
                <div
                  key={item.id}
                  className="relative bg-white rounded shadow-md cursor-pointer"
                  onClick={() => openDetail(item)}
                >
                  <span
                    className={`absolute top-2 left-2 px-2 py-1 text-xs rounded text-white ${
                      normalize(item.status) === "available"
                        ? "bg-green-600"
                        : "bg-yellow-600"
                    }`}
                  >
                    {item.status}
                  </span>

                  <div className="absolute top-2 right-2 px-2 py-1 text-xs rounded text-white bg-gray-800">
                    {`Stock: ${stockTotal}`}
                  </div>

                  <div className="absolute top-10 right-2 flex flex-col gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleWish(item);
                      }}
                      className={`text-xl transition-colors duration-200 ${
                        isWished
                          ? "text-red-600"
                          : "text-rose-300 hover:text-red-300"
                      }`}
                    >
                      <FaHeart /> {item.wishes || 0}
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        shareItem(item);
                      }}
                      className="text-xl text-green-600 hover:text-green-800 transition-colors duration-200"
                    >
                      <FaShareAlt />
                    </button>
                  </div>

                  <img
                    src={getImageForColor(
                      item,
                      getName(item.colors?.[0]) || item.colors?.[0]
                    )}
                    alt={item.name}
                    className="w-full h-48 object-cover rounded-t"
                  />

                  <div className="p-2 flex flex-col gap-2">
                    <p className="text-center font-bold text-gray-800">
                      {capitalizeWords(item.name)}
                    </p>
                    <p className="text-center text-gray-700 font-semibold">
                      ₦
                      {Number(
                        item.priceDiscounted ?? item.priceOriginal ?? 0
                      ).toLocaleString()}
                    </p>

                    <button
                      onClick={(e) => handleGridAddClick(e, item)}
                      disabled={loadingItemId === item.id}
                      className={`px-3 py-1 rounded text-white ${
                        loadingItemId === item.id
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-purple-700 hover:bg-purple-800"
                      }`}
                    >
                      {loadingItemId === item.id
                        ? "Adding..."
                        : itemHasVariants
                        ? "Select Options"
                        : "Add to Cart"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {visibleCount < itemsToDisplay.length && (
            <div className="flex justify-center mt-6">
              <button
                onClick={() => setVisibleCount((prev) => prev + 6)}
                className="px-6 py-2 bg-purple-700 text-white rounded-md hover:bg-purple-800 transition-colors"
              >
                Load More
              </button>
            </div>
          )}
        </>
      )}

      {/* Detail Modal */}
      {selectedItem && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => e.target === e.currentTarget && setSelectedItem(null)}
        >
          <div
            className="bg-white p-6 rounded-lg max-w-lg w-full relative shadow-xl overflow-y-auto max-h-[90vh] pb-[80px]"
            role="dialog"
            aria-modal="true"
          >
            <button
              onClick={() => {
                setSelectedItem(null);
                setShowAddedModalItemId(null);
                setLastVariantSelection(null);
              }}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>

            <h2 className="text-2xl font-bold mb-2 text-center">
              {capitalizeWords(selectedItem.name)}
            </h2>

            <AnimatePresence mode="wait">
              <motion.img
                key={`${selectedItem.id}-${activeColor || "noc"}-${activeSize || "nos"}`}
                src={getImageForVariant(
                  selectedItem,
                  activeColor || getName(selectedItem.colors?.[0]) || null,
                  activeSize || null,
                  lastVariantSelection
                )}
                alt={selectedItem.name}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.35 }}
                className="w-full h-64 object-cover rounded mb-4"
              />
            </AnimatePresence>

            {/* Circular color swatches */}
            {Array.isArray(selectedItem.colors) &&
              selectedItem.colors.length > 0 && (
                <div className="mb-3">
                  <p className="text-sm font-semibold">Pick color:</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {selectedItem.colors.map((color, idx) => {
                      const name = getName(color) || `color-${idx}`;
                      const colorImg = selectedItem.colorImages?.[name];
                      return (
                        <button
                          key={name}
                          onClick={() => {
                            setActiveColor(name);
                            setLastVariantSelection("color");
                            const newStock = getVariantQuantity(
                              selectedItem,
                              name,
                              activeSize,
                              "color"
                            );
                            setSelectedQty((q) => clampQtyToStock(q, newStock));
                          }}
                          title={name}
                          className={`w-8 h-8 rounded-full border-2 flex items-center justify-center overflow-hidden ${
                            activeColor === name
                              ? "border-black scale-110"
                              : "border-gray-300"
                          } transition-transform`}
                          style={{ padding: 0 }}
                        >
                          {colorImg ? (
                            <img
                              src={colorImg}
                              alt={name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span
                              className="w-full h-full block"
                              style={{ backgroundColor: name }}
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

            <p className="mb-4 text-gray-700 leading-relaxed">
              {selectedItem.description}
            </p>

            {/* Combined dropdown row */}
            <div className="mb-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {Array.isArray(selectedItem.colors) &&
                  selectedItem.colors.length > 0 && (
                    <div>
                      <p className="text-sm font-semibold mb-2">Colors:</p>
                      <select
                        value={activeColor || ""}
                        onChange={(e) => {
                          const nextColor = e.target.value || null;
                          setActiveColor(nextColor);
                          setLastVariantSelection("color");
                          const newStock = getVariantQuantity(
                            selectedItem,
                            nextColor,
                            activeSize,
                            "color"
                          );
                          setSelectedQty((q) => clampQtyToStock(q, newStock));
                        }}
                        className="w-full border px-3 py-2 rounded"
                      >
                        <option value="">Select color</option>
                        {selectedItem.colors.map((color, idx) => {
                          const name = getName(color) || `color-${idx}`;
                          const price =
                            color?.priceDiscounted ??
                            color?.priceOriginal ??
                            selectedItem.priceDiscounted;
                          const labelPrice = price
                            ? ` - ₦${Number(price).toLocaleString()}`
                            : "";
                          return (
                            <option key={name} value={name}>
                              {name}
                              {labelPrice}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  )}

                {Array.isArray(selectedItem?.sizes) &&
                  selectedItem.sizes.length > 0 && (
                    <div>
                      <p className="text-sm font-semibold mb-2">Sizes:</p>

                      <select
                        value={activeSize || ""}
                        onChange={(e) => {
                          const value = e.target.value || null;
                          setActiveSize(value);
                          setLastVariantSelection("size");
                          const newStock = getVariantQuantity(
                            selectedItem,
                            activeColor,
                            value,
                            "size"
                          );
                          setSelectedQty((q) => clampQtyToStock(q, newStock));
                        }}
                        className="w-full border px-3 py-2 rounded"
                      >
                        <option value="">Select size</option>

                        {selectedItem.sizes.map((size, idx) => {
                          const name = getName(size) || `size-${idx}`;
                          const price =
                            size?.priceDiscounted ??
                            size?.priceOriginal ??
                            selectedItem.priceDiscounted ??
                            selectedItem.priceOriginal ??
                            null;

                          const labelPrice = price
                            ? ` - ₦${Number(price).toLocaleString()}`
                            : "";

                          return (
                            <option key={name} value={name}>
                              {name}
                              {labelPrice}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  )}
              </div>
            </div>

            <p className="mb-2 text-sm italic text-gray-600">
              Category: {selectedItem.category}
            </p>

            <p className="mb-2 text-sm font-semibold">
              Status:{" "}
              <span
                className={`px-2 py-1 text-xs rounded text-white ${
                  normalize(selectedItem.status) === "available"
                    ? "bg-green-600"
                    : "bg-yellow-600"
                }`}
              >
                {selectedItem.status}
              </span>
            </p>

            <p className="mb-2 text-sm font-semibold">
              Stock:{" "}
              <span className="px-2 py-1 text-xs rounded bg-gray-800 text-white">
                {getVariantQuantity(
                  selectedItem,
                  activeColor,
                  activeSize,
                  lastVariantSelection
                )}
              </span>
            </p>

            <p className="mb-2 text-sm font-semibold">
              Tracking Stage:{" "}
              <span
                className={`px-2 py-1 text-xs rounded text-white ${
                  (selectedItem.trackingStage || "").toLowerCase().includes("deliv")
                    ? "bg-green-600"
                    : (selectedItem.trackingStage || "")
                        .toLowerCase()
                        .includes("ship")
                    ? "bg-blue-600"
                    : "bg-gray-500"
                }`}
              >
                {selectedItem.trackingStage || "Pending"}
              </span>
            </p>

            <p className="mt-2 text-sm text-gray-600">
              {normalize(selectedItem.status) === "available"
                ? "✅ This item is in stock and ready for delivery."
                : "🕒 This item will be available in 2-3 months’ time."}
            </p>

            {/* Variant price display */}
            <p className="mb-2 text-gray-400 line-through">
              Original: ₦{selectedItem.priceOriginal?.toLocaleString()}
            </p>
            <p className="font-semibold text-purple-700 text-lg mb-2">
              Discount: ₦
              {(
                getVariantPrice(
                  selectedItem,
                  activeColor,
                  activeSize,
                  lastVariantSelection
                ) ?? selectedItem.priceDiscounted
              )?.toLocaleString()}
            </p>

            {selectedItem.shipmentFee && (
              <p className="text-sm text-gray-700 mb-4">
                🚚 Shipment Fee: ₦{selectedItem.shipmentFee}{" "}
                <span className="italic text-gray-500">(paid separately)</span>
              </p>
            )}

            {/* Qty picker */}
            <div className="mt-3 flex items-center gap-3">
              <p className="text-sm font-semibold">Quantity:</p>

              <button
                type="button"
                className="px-3 py-1 border rounded"
                onClick={() =>
                  setSelectedQty((q) => Math.max(1, Number(q || 1) - 1))
                }
              >
                -
              </button>

              <input
                type="number"
                min="1"
                value={selectedQty}
                onChange={(e) => {
                  const stock = getSelectedItemCurrentStock(selectedItem);
                  const next = clampQtyToStock(e.target.value, stock);
                  setSelectedQty(next);
                  if (Number(e.target.value) > stock) {
                    toast.info(`Only ${stock} left in stock for this selection.`);
                  }
                }}
                className="w-20 border px-2 py-1 rounded text-center"
              />

              <button
                type="button"
                className="px-3 py-1 border rounded"
                onClick={() => {
                  const stock = getSelectedItemCurrentStock(selectedItem);
                  setSelectedQty((q) => {
                    const next = Number(q || 1) + 1;
                    if (next > stock) {
                      toast.info(`Only ${stock} left in stock for this selection.`);
                      return stock > 0 ? stock : 1;
                    }
                    return next;
                  });
                }}
              >
                +
              </button>

              <span className="text-xs text-gray-500 ml-auto">
                Max: {getSelectedItemCurrentStock(selectedItem)}
              </span>
            </div>

            <div className="flex flex-col gap-3 mt-6">
              {showAddedModalItemId === selectedItem.id ? (
                <div className="flex flex-col sm:flex-row gap-3 mt-6">
                  <button
                    onClick={() => {
                      setSelectedItem(null);
                      setShowAddedModalItemId(null);
                      toggleCart();
                    }}
                    className="flex-1 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                  >
                    Go to Cart 🛒
                  </button>

                  <button
                    onClick={() => {
                      setShowAddedModalItemId(null);
                      setSelectedItem(null);
                      setLastVariantSelection(null);
                      setTimeout(() => {
                        window.scrollTo({
                          top: savedScroll,
                          behavior: "smooth",
                        });
                      }, 300);
                    }}
                    className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300"
                  >
                    Continue Shopping
                  </button>
                </div>
              ) : (
                <button
                  onClick={async () => {
                    const chosenColor =
                      activeColor ||
                      (selectedItem.colors?.length
                        ? getName(selectedItem.colors[0])
                        : null);
                    const chosenSize =
                      activeSize ||
                      (selectedItem.sizes?.length
                        ? getName(selectedItem.sizes[0])
                        : null);

                    const ok = await handleAddToCart(
                      selectedItem,
                      chosenColor,
                      chosenSize,
                      selectedQty || 1
                    );

                    if (ok) setShowAddedModalItemId(selectedItem.id);
                  }}
                  disabled={loadingItemId === selectedItem.id}
                  className={`px-4 py-2 rounded text-white ${
                    loadingItemId === selectedItem.id
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-purple-700 hover:bg-purple-800"
                  }`}
                >
                  {loadingItemId === selectedItem.id
                    ? "Adding..."
                    : `Add ${selectedQty} to Cart`}
                </button>
              )}
            </div>

            <div className="mt-6 border-t pt-4 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <button
                  onClick={() => handleWish(selectedItem)}
                  className={`flex-1 flex items-center justify-center gap-2 border rounded-md py-2 text-sm font-semibold transition-colors ${
                    (currentUser?.uid &&
                      (selectedItem.wishedBy || []).includes(currentUser.uid)) ||
                    localWishlist.includes(selectedItem.id)
                      ? "text-red-600 border-red-400 bg-white"
                      : "text-gray-600 border-gray-300 bg-white hover:text-red-400"
                  }`}
                >
                  <FaHeart className="text-lg" />
                  Wishlist
                </button>

                <button
                  onClick={() => shareItem(selectedItem)}
                  className="flex-1 flex items-center justify-center gap-2 border border-gray-300 bg-white rounded-md py-2 text-sm font-semibold text-gray-600 hover:text-green-600 transition-colors"
                >
                  <FaShareAlt className="text-lg" />
                  Share
                </button>
              </div>

              <div className="flex justify-center gap-5 text-xl mt-2">
                <a href="#" className="text-blue-600 hover:scale-110 transition">
                  <FaFacebook />
                </a>
                <a
                  href="#"
                  className="text-green-500 hover:scale-110 transition"
                >
                  <FaWhatsapp />
                </a>
                <a href="#" className="text-sky-500 hover:scale-110 transition">
                  <FaTwitter />
                </a>
                <a href="#" className="text-pink-500 hover:scale-110 transition">
                  <FaInstagram />
                </a>
              </div>

              <button className="w-full bg-gray-100 text-gray-800 py-2 rounded-md font-semibold border-b-2 border-purple-600 hover:bg-gray-200 transition-colors">
                Reviews (0)
              </button>
            </div>

            {/* Admin Controls */}
            {isAdmin && (
              <div className="mt-6 border-t pt-4 space-y-3">
                <h3 className="font-semibold text-gray-700 mb-2">
                  Admin Controls
                </h3>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => {
                      setEditingCookingItem(selectedItem);
                      setNewCookingItem({
                        ...selectedItem,
                        image: selectedItem.image || null,
                        trackingStage: selectedItem.trackingStage || "Pending",
                        quantity: selectedItem.quantity ?? 0,
                        restockAmount: 0,
                        colorImages: selectedItem.colorImages || {},
                        sizeImages: selectedItem.sizeImages || {},
                      });
                      setColorFiles({});
                      setSizeFiles({});
                      setAddModalCustomVisible(false);
                      setShowAddModal(true);
                    }}
                    className="flex-1 bg-purple-700 text-white px-4 py-2 rounded hover:bg-purple-800 transition"
                  >
                    Edit Product
                  </button>

                  <button
                    onClick={() => toggleVisibility(selectedItem)}
                    className={`flex-1 px-4 py-2 rounded text-white transition ${
                      selectedItem.available
                        ? "bg-yellow-600 hover:bg-yellow-700"
                        : "bg-green-600 hover:bg-green-700"
                    }`}
                  >
                    {selectedItem.available ? "Hide Product" : "Show Product"}
                  </button>

                  <button
                    onClick={() => confirmDeleteToast(selectedItem)}
                    disabled={deletingItemId === selectedItem.id}
                    className={`flex-1 px-4 py-2 rounded text-white transition ${
                      deletingItemId === selectedItem.id
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-red-600 hover:bg-red-700"
                    }`}
                  >
                    {deletingItemId === selectedItem.id
                      ? "Deleting..."
                      : "Delete Product"}
                  </button>
                </div>

                {/* Quick Tracking Stage */}
                <div className="mt-3">
                  <label className="text-sm font-semibold mb-2 block">
                    Quick Tracking Stage
                  </label>
                  <div className="flex gap-2 items-center">
                    <select
                      value={selectedItem.trackingStage || "Pending"}
                      onChange={async (e) => {
                        const val = e.target.value;
                        if (val === "Custom") {
                          setCustomTrackingInput("");
                          setSelectedItem((prev) => ({
                            ...prev,
                            trackingStage: "Custom",
                          }));
                          return;
                        }
                        await updateSelectedItemTracking(val);
                      }}
                      className="border px-3 py-2 rounded"
                    >
                      <option value="Pending">Pending</option>
                      <option value="Processing">Processing</option>
                      <option value="Packed">Packed</option>
                      <option value="Shipped (ReadyForDelivery)">
                        Shipped (ReadyForDelivery)
                      </option>
                      <option value="Out for Delivery">Out for Delivery</option>
                      <option value="Delivered">Delivered</option>
                      <option value="Custom">Custom</option>
                    </select>

                    {(selectedItem.trackingStage === "Custom" ||
                      selectedItem.trackingStage === "Custom") && (
                        <div className="flex gap-2 items-center">
                          <input
                            type="text"
                            placeholder="Enter custom tracking stage"
                            value={customTrackingInput}
                            onChange={(e) => setCustomTrackingInput(e.target.value)}
                            className="border px-3 py-2 rounded"
                          />
                          <button
                            onClick={async () => {
                              if (!customTrackingInput || !customTrackingInput.trim()) {
                                toast.error("Please provide a custom tracking stage");
                                return;
                              }
                              await updateSelectedItemTracking(
                                customTrackingInput.trim()
                              );
                              setCustomTrackingInput("");
                            }}
                            className="bg-purple-700 text-white px-3 py-2 rounded hover:bg-purple-800"
                          >
                            Save Tracking
                          </button>
                        </div>
                      )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4"
          onClick={(e) => e.target === e.currentTarget && setShowAddModal(false)}
        >
          <div className="bg-white p-6 rounded shadow-md w-full max-w-md overflow-y-auto max-h-[90vh]">
            <h3 className="text-xl font-bold mb-4 text-gray-800">
              {editingCookingItem ? "Edit Product" : "Add Product"}
            </h3>

            {(() => {
              const hasVariantsLocal =
                (newCookingItem.colors?.length || 0) > 0 ||
                (newCookingItem.sizes?.length || 0) > 0;

              return (
                <>
                  <input
                    name="name"
                    type="text"
                    placeholder="Product Name"
                    value={newCookingItem.name}
                    onChange={handleInputChange}
                    className="w-full mb-2 border px-3 py-2 rounded"
                  />

                  <textarea
                    name="description"
                    placeholder="Description"
                    value={newCookingItem.description}
                    onChange={handleInputChange}
                    className="w-full mb-2 border px-3 py-2 rounded"
                  />

                  {!hasVariantsLocal && (
                    <>
                      <input
                        name="priceOriginal"
                        type="number"
                        placeholder="Original Price (₦)"
                        value={newCookingItem.priceOriginal}
                        onChange={handleInputChange}
                        className="w-full mb-2 border px-3 py-2 rounded"
                      />

                      <input
                        name="priceDiscounted"
                        type="number"
                        placeholder="Discounted Price (₦)"
                        value={newCookingItem.priceDiscounted}
                        onChange={handleInputChange}
                        className="w-full mb-2 border px-3 py-2 rounded"
                      />
                    </>
                  )}

                  <input
                    name="shipmentFee"
                    type="number"
                    placeholder="Shipment Fee (₦ - paid separately)"
                    value={newCookingItem.shipmentFee}
                    onChange={handleInputChange}
                    className="w-full mb-2 border px-3 py-2 rounded"
                  />

                  <div className="mb-2">
                    <input
                      name="image"
                      type="file"
                      onChange={handleInputChange}
                      className="mb-2 w-full"
                    />
                    {editingCookingItem?.image &&
                      typeof editingCookingItem.image === "string" && (
                        <img
                          src={editingCookingItem.image}
                          alt="preview"
                          className="w-28 h-28 object-cover rounded"
                        />
                      )}
                  </div>

                  <select
                    name="category"
                    value={newCookingItem.category}
                    onChange={handleInputChange}
                    className="w-full mb-2 border px-3 py-2 rounded"
                  >
                    <option value="">Select Category</option>
                    {(categories || []).map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>

                  {/* COLORS */}
                  <div className="mb-3">
                    <label className="block text-sm font-semibold mb-1">
                      Colors
                    </label>

                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={colorInput}
                        onChange={(e) => setColorInput(e.target.value)}
                        placeholder="Add color (e.g. red or #ff0000)"
                        className="flex-grow border px-3 py-2 rounded"
                      />
                      <button
                        type="button"
                        onClick={() => addArrayValue("colors", colorInput)}
                        className="bg-green-600 text-white px-3 py-2 rounded"
                      >
                        +
                      </button>
                    </div>

                    <div className="flex flex-col gap-2 mt-2">
                      {(newCookingItem.colors || []).map((c) => {
                        const cname = getName(c);
                        return (
                          <div key={cname} className="flex items-center gap-2">
                            <span className="px-2 py-1 bg-gray-200 rounded w-20">
                              {cname}
                            </span>

                            <input
                              type="number"
                              placeholder="Original ₦"
                              value={c.priceOriginal}
                              onChange={(e) =>
                                setNewCookingItem((prev) => ({
                                  ...prev,
                                  colors: prev.colors.map((col) =>
                                    getName(col) === cname
                                      ? { ...col, priceOriginal: e.target.value }
                                      : col
                                  ),
                                }))
                              }
                              className="border px-2 py-1 w-24 rounded"
                            />

                            <input
                              type="number"
                              placeholder="Discount ₦"
                              value={c.priceDiscounted}
                              onChange={(e) =>
                                setNewCookingItem((prev) => ({
                                  ...prev,
                                  colors: prev.colors.map((col) =>
                                    getName(col) === cname
                                      ? {
                                          ...col,
                                          priceDiscounted: e.target.value,
                                        }
                                      : col
                                  ),
                                }))
                              }
                              className="border px-2 py-1 w-24 rounded"
                            />

                            <input
                              type="number"
                              min="0"
                              placeholder="Qty"
                              value={c.quantity}
                              readOnly
                              onFocus={handleQtyEditAttempt}
                              className="border px-2 py-1 w-20 rounded bg-gray-50 cursor-not-allowed"
                              title="To change variant stock go to Inventory → Restock this variant"
                            />

                            <input
                              name={`color-file-${cname}`}
                              type="file"
                              onChange={handleInputChange}
                              className="w-24"
                            />

                            {(() => {
                              const previewUrl = colorPreviewUrls?.[cname];
                              const savedUrl =
                                newCookingItem.colorImages?.[cname];
                              const shown = previewUrl || savedUrl || null;

                              return shown ? (
                                <img
                                  src={shown}
                                  alt={`${cname} preview`}
                                  className="w-10 h-10 object-cover rounded border"
                                />
                              ) : (
                                <div className="w-10 h-10 rounded border bg-gray-50 flex items-center justify-center text-[10px] text-gray-400">
                                  no img
                                </div>
                              );
                            })()}

                            <button
                              type="button"
                              onClick={() => removeArrayValue("colors", cname)}
                              className="px-2 py-1 bg-red-500 text-white rounded"
                            >
                              ✕
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* SIZES */}
                  <div className="mb-3">
                    <label className="block text-sm font-semibold mb-1">
                      Sizes
                    </label>

                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={sizeInput}
                        onChange={(e) => setSizeInput(e.target.value)}
                        placeholder="Add size (e.g. S, M, L)"
                        className="flex-grow border px-3 py-2 rounded"
                      />
                      <button
                        type="button"
                        onClick={() => addArrayValue("sizes", sizeInput)}
                        className="bg-green-600 text-white px-3 py-2 rounded"
                      >
                        +
                      </button>
                    </div>

                    <div className="flex flex-col gap-2 mt-2">
                      {(newCookingItem.sizes || []).map((s) => {
                        const sname = getName(s);
                        return (
                          <div key={sname} className="flex items-center gap-2">
                            <span className="px-2 py-1 bg-gray-200 rounded w-20">
                              {sname}
                            </span>

                            <input
                              type="number"
                              placeholder="Original ₦"
                              value={s.priceOriginal}
                              onChange={(e) =>
                                setNewCookingItem((prev) => ({
                                  ...prev,
                                  sizes: prev.sizes.map((sz) =>
                                    getName(sz) === sname
                                      ? { ...sz, priceOriginal: e.target.value }
                                      : sz
                                  ),
                                }))
                              }
                              className="border px-2 py-1 w-24 rounded"
                            />

                            <input
                              type="number"
                              placeholder="Discount ₦"
                              value={s.priceDiscounted}
                              onChange={(e) =>
                                setNewCookingItem((prev) => ({
                                  ...prev,
                                  sizes: prev.sizes.map((sz) =>
                                    getName(sz) === sname
                                      ? {
                                          ...sz,
                                          priceDiscounted: e.target.value,
                                        }
                                      : sz
                                  ),
                                }))
                              }
                              className="border px-2 py-1 w-24 rounded"
                            />

                            <input
                              type="number"
                              min="0"
                              placeholder="Qty"
                              value={s.quantity}
                              readOnly
                              onFocus={handleQtyEditAttempt}
                              className="border px-2 py-1 w-20 rounded bg-gray-50 cursor-not-allowed"
                              title="To change variant stock go to Inventory → Restock this variant"
                            />

                            <input
                              name={`size-file-${sname}`}
                              type="file"
                              onChange={handleInputChange}
                              className="w-24"
                            />

                            {(() => {
                              const previewUrl = sizePreviewUrls?.[sname];
                              const savedUrl = newCookingItem.sizeImages?.[sname];
                              const shown = previewUrl || savedUrl || null;

                              return shown ? (
                                <img
                                  src={shown}
                                  alt={`${sname} preview`}
                                  className="w-10 h-10 object-cover rounded border"
                                />
                              ) : (
                                <div className="w-10 h-10 rounded border bg-gray-50 flex items-center justify-center text-[10px] text-gray-400">
                                  no img
                                </div>
                              );
                            })()}

                            <button
                              type="button"
                              onClick={() => removeArrayValue("sizes", sname)}
                              className="px-2 py-1 bg-red-500 text-white rounded"
                            >
                              ✕
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* AVAILABILITY */}
                  <div className="flex gap-2 items-center mb-3">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        name="available"
                        checked={!!newCookingItem.available}
                        onChange={handleInputChange}
                      />{" "}
                      Visible to Customers
                    </label>

                    <select
                      name="status"
                      value={newCookingItem.status}
                      onChange={handleInputChange}
                      className="ml-auto border px-2 py-1 rounded"
                    >
                      <option value="Available">Available</option>
                      <option value="Pre-Order">Pre-Order</option>
                    </select>
                  </div>

                  {/* STOCK (ONLY IF NO VARIANTS) */}
                  {!hasVariantsLocal && !editingCookingItem && (
                    <div className="mb-3">
                      <label className="block text-sm font-semibold mb-1">
                        Initial Stock Quantity
                      </label>
                      <input
                        name="quantity"
                        type="number"
                        min="0"
                        value={newCookingItem.quantity}
                        readOnly
                        onFocus={handleQtyEditAttempt}
                        className="w-full border px-3 py-2 rounded bg-gray-50 cursor-not-allowed"
                        title="To change stock quantities please use Inventory → Restock"
                      />
                    </div>
                  )}

                  {!hasVariantsLocal && editingCookingItem && (
                    <div className="mb-3">
                      <label className="block text-sm font-semibold mb-1">
                        Restock Amount
                      </label>
                      <input
                        name="restockAmount"
                        type="number"
                        min="0"
                        value={newCookingItem.restockAmount}
                        onChange={handleInputChange}
                        className="w-full border px-3 py-2 rounded"
                      />
                    </div>
                  )}

                  {/* TRACKING */}
                  <div className="mb-3">
                    <label className="block text-sm font-semibold mb-1">
                      Tracking Stage
                    </label>
                    <select
                      name="trackingStage"
                      value={newCookingItem.trackingStage || "Pending"}
                      onChange={handleInputChange}
                      className="w-full border px-3 py-2 rounded"
                    >
                      <option value="Pending">Pending</option>
                      <option value="Processing">Processing</option>
                      <option value="Packed">Packed</option>
                      <option value="Shipped (ReadyForDelivery)">
                        Shipped (ReadyForDelivery)
                      </option>
                      <option value="Out for Delivery">Out for Delivery</option>
                      <option value="Delivered">Delivered</option>
                      <option value="Custom">Custom</option>
                    </select>
                  </div>

                  {/* ACTIONS */}
                  <div className="flex justify-end gap-2 mt-4">
                    <button
                      onClick={() => {
                        setShowAddModal(false);
                        setAddModalCustomVisible(false);
                      }}
                      className="px-4 py-2 bg-gray-300 rounded"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSubmitCookingItem}
                      disabled={isSavingProduct}
                      className={`px-4 py-2 text-white rounded ${
                        isSavingProduct
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-purple-700 hover:bg-purple-800"
                      }`}
                    >
                      {isSavingProduct
                        ? editingCookingItem
                          ? "Updating..."
                          : "Adding..."
                        : editingCookingItem
                        ? "Update"
                        : "Add"}
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}